import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  formatAction,
  formatRole,
  formatResult,
  formatIp,
  formatResourceType,
} from "utils/auditLogs";
import AuditLogSecurity from "./AuditLogSecurity";

const AuditLogView = ({ item, handleView }) => {
  const { t } = useTranslation();

  const getBadgeClass = (result) => {
    switch (result) {
      case "SUCCESS":
        return "bg-green-100 text-green-800";
      case "DENIED":
        return "bg-red-100 text-red-800";
      case "FAILURE":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCompanyId = (company) => {
    if (!company) return null;
    if (typeof company === "object") return company.id || null;
    return company;
  };

  const rawRole = item?.actor?.role || item?.actorRole;
  const companyId = getCompanyId(item.companyId);

  const formattedAction = formatAction(item.action) || "";
  const displayAction =
    !companyId && formattedAction.toLowerCase().includes("employee")
      ? formattedAction.replace(/employee/gi, "Candidate")
      : formattedAction;

  const actorRole = rawRole
    ? formatRole(rawRole)
    : !companyId && item.action === "EMPLOYEE_LOGIN"
      ? "Candidate"
      : formatRole(rawRole);

  // ─── PRIORITIZE DISPLAY NAMES FROM BACKEND ───
  const actorName =
    item?.actorDisplayName || item?.actor?.id || item?.actorId || null;
  const actorId = item?.actor?.id || item?.actorId || null;

  const resourceName =
    item?.resourceDisplayName ||
    (item.resourceId && item.resourceId !== "undefined"
      ? item.resourceId
      : null);
  const resourceId =
    item.resourceId && item.resourceId !== "undefined" ? item.resourceId : null;

  const companyName = item?.companyDisplayName || companyId || null;
  const rawCompanyId = companyId;

  const resourceType =
    item.resourceType && item.resourceType !== "employee"
      ? formatResourceType(item.resourceType)
      : null;

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <div className="md:py-4 sm:px-2 sm:py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[762px] max-h-[90vh]">
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">
                  {t("AUDIT_LOG_DETAILS")}
                </h3>
                <button
                  className="ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                  onClick={() => handleView(false)}
                >
                  <span
                    className="text-[#B8BBBF] text-4xl"
                    onClick={() => handleView()}
                    title="Close"
                  >
                    ×
                  </span>
                </button>
              </div>

              <div className="relative p-6 flex-auto overflow-y-auto">
                {/* SINGLE GRID CONTAINER WITH NO NESTED GRIDS */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Element 1: Action */}
                  <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                    <strong>{t("ACTION")}:</strong>
                    <h6 className="mt-1 font-medium">{displayAction}</h6>
                  </div>

                  {/* Element 2: Resource Type */}
                  {resourceType && (
                    <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                      <strong>{t("RESOURCE_TYPE")}:</strong>
                      <h6 className="mt-1 font-medium">{resourceType}</h6>
                    </div>
                  )}

                  {/* Element 3: Resource ID / Name */}
                  {resourceName && (
                    <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                      <strong>{t("RESOURCE_ID")}:</strong>
                      <h6 className="mt-1 font-medium break-all">
                        {resourceName}
                        {resourceId && resourceId !== resourceName && (
                          <span className="block text-gray-500 text-xs mt-1">
                            {resourceId}
                          </span>
                        )}
                      </h6>
                    </div>
                  )}

                  {/* Element 4: Actor ID / Name */}
                  {actorName && (
                    <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                      <strong>{t("ACTOR_ID")}:</strong>
                      <h6 className="mt-1 font-medium break-all">
                        {actorName}
                        {actorId && actorId !== actorName && (
                          <span className="block text-gray-500 text-xs mt-1">
                            {actorId}
                          </span>
                        )}
                      </h6>
                    </div>
                  )}

                  {/* Element 5: Actor Role */}
                  {actorRole && (
                    <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                      <strong>{t("ACTOR_ROLE")}:</strong>
                      <h6 className="mt-1 font-medium">
                        {formatRole(actorRole)}
                      </h6>
                    </div>
                  )}

                  {/* Element 6: Company ID / Name */}
                  {companyName && (
                    <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                      <strong>{t("COMPANY_ID")}:</strong>
                      <h6 className="mt-1 font-medium break-all">
                        {companyName}
                        {rawCompanyId &&
                          String(rawCompanyId) !== companyName && (
                            <span className="block text-gray-500 text-xs mt-1">
                              {String(rawCompanyId)}
                            </span>
                          )}
                      </h6>
                    </div>
                  )}

                  {/* Element 7: IP Address */}
                  {item.ip && (
                    <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                      <strong>{t("IP_ADDRESS")}:</strong>
                      <h6 className="mt-1 font-medium">{formatIp(item.ip)}</h6>
                    </div>
                  )}

                  {/* Element 8: Created Date */}
                  <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                    <strong>{t("CREATED_DATE")}:</strong>
                    <h6 className="mt-1 font-medium">
                      {item.createdAt
                        ? dayjs(item.createdAt).format("MM-DD-YYYY hh:mm:ss A")
                        : "-"}
                    </h6>
                  </div>

                  {/* Element 9: User Agent */}
                  {item.userAgent && (
                    <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                      <strong>{t("USER_AGENT")}:</strong>
                      <h6 className="mt-1 font-medium break-all text-xs">
                        {item.userAgent}
                      </h6>
                    </div>
                  )}

                  {/* Element 10: Result */}
                  <div className="sm:p-4 p-8 bg-slate-100 rounded-md">
                    <strong>{t("RESULT")}:</strong>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${getBadgeClass(item.result)}`}
                    >
                      {formatResult(item.result)}
                    </span>
                  </div>
                  <AuditLogSecurity item={item} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default AuditLogView;
